import { useEffect } from 'react';
import { X } from 'lucide-react';

export default function Modal({
  open, onClose, title, subtitle, children, footer, size = 'md', closable = true,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && closable) onClose(); };
    window.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while the dialog is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, closable]);

  if (!open) return null;

  const sizes = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-content/40 p-2 pt-[3vh] backdrop-blur-[2px] animate-fade-in sm:p-4 sm:pt-[7vh]"
      onMouseDown={(e) => { if (closable && e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      <div
        className={`w-full animate-slide-up rounded-xl border border-line bg-surface shadow-pop ${sizes[size] || sizes.md}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3 sm:gap-4 sm:px-5 sm:py-3.5">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-content">{title}</h3>
            {subtitle && <p className="mt-0.5 truncate text-xs text-content-muted">{subtitle}</p>}
          </div>
          {closable && (
            <button onClick={onClose} className="btn-icon -mr-1 shrink-0" aria-label="Close">
              <X size={17} />
            </button>
          )}
        </div>

        {/* Taller share of a short phone screen, and `dvh` so the browser's
            own chrome does not hide the footer. */}
        <div className="max-h-[75dvh] overflow-y-auto px-4 py-4 sm:max-h-[68vh] sm:px-5">{children}</div>

        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-xl border-t border-line bg-surface-sunken px-4 py-3 sm:px-5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
